import React, { useEffect, useState } from "react";
import { Confirm, Input, Label, Modal, RadioButton, RadioButtonList, Spinner } from "@tag/tag-components-react-v4";
import CustomerService from "../../services/CustomerService";
import { CustomerDTO } from "../../types/CustomerDTO";
import { ProjectAccents } from "../../../General/config/projectAccents";

import styles from "./CareConnectConfigurationModal.module.scss";

type CareConnectEdition = "full" | "lite";

const EDITION_DESCRIPTIONS: Record<CareConnectEdition, string> = {
  full: "The complete Care Connect feature set is available to this customer.",
  lite: "A reduced Care Connect feature set (e.g. No AI Email Automation)."
};

export const DEFAULT_DAILY_EMAIL_PROCESSING_LIMIT = 100;

interface CareConnectConfigurationModalProps {
  visible: boolean;
  selectedCustomer: CustomerDTO;
  handleClose: Function;
  setAlert: Function;
  handleCustomerMenuModalClose: Function;
}

export const CareConnectConfigurationModal: React.FC<CareConnectConfigurationModalProps> = ({
  visible,
  selectedCustomer,
  handleClose,
  setAlert,
  handleCustomerMenuModalClose
}) => {
  const [currentEdition, setCurrentEdition] = useState<CareConnectEdition>("full");
  const [selectedEdition, setSelectedEdition] = useState<CareConnectEdition>("full");
  const [currentLimit, setCurrentLimit] = useState(DEFAULT_DAILY_EMAIL_PROCESSING_LIMIT);
  const [limitInput, setLimitInput] = useState(DEFAULT_DAILY_EMAIL_PROCESSING_LIMIT.toString());
  const [hasConfiguration, setHasConfiguration] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchConfiguration = async () => {
      try {
        setLoading(true);
        const data = await CustomerService.getCareConnectConfiguration(
          selectedCustomer.clientId,
          selectedCustomer.environmentName
        );

        if (!isMounted) {
          return;
        }

        const edition: CareConnectEdition = data.isLiteEdition ? "lite" : "full";
        const limit = data.dailyEmailProcessingLimit ?? DEFAULT_DAILY_EMAIL_PROCESSING_LIMIT;
        setCurrentEdition(edition);
        setSelectedEdition(edition);
        setCurrentLimit(limit);
        setLimitInput(limit.toString());
        setHasConfiguration(data.hasConfiguration);
        setLoadFailed(false);
      } catch (error) {
        console.error("Error fetching Care Connect configuration:", error);
        if (isMounted) {
          setLoadFailed(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchConfiguration();

    return () => {
      isMounted = false;
    };
  }, [selectedCustomer.clientId, selectedCustomer.environmentName]);

  const parsedLimit = /^\d+$/.test(limitInput.trim()) ? parseInt(limitInput.trim(), 10) : undefined;
  const limitIsValid = parsedLimit !== undefined && parsedLimit > 0;
  const isDirty = selectedEdition !== currentEdition || parsedLimit !== currentLimit;
  const canSave = limitIsValid && (isDirty || !hasConfiguration);

  const handleSave = async () => {
    if (!limitIsValid) {
      return;
    }

    setConfirmVisible(false);
    setSaving(true);

    try {
      await CustomerService.updateCareConnectConfiguration(
        selectedCustomer.clientId,
        { isLiteEdition: selectedEdition === "lite", dailyEmailProcessingLimit: parsedLimit as number },
        selectedCustomer.environmentName
      );

      setAlert({ type: "success", message: "Care Connect configuration saved successfully" });
      handleClose();
      handleCustomerMenuModalClose();
    } catch (error) {
      console.error("Error updating Care Connect configuration:", error);
      setAlert({ type: "error", message: "Failed to save the Care Connect configuration. Please try again" });
      handleClose();
      handleCustomerMenuModalClose();
    } finally {
      setSaving(false);
    }
  };

  let content;
  if (loading || saving) {
    content = (
      <div className={styles.spinnerContainer}>
        <Spinner size="medium">{saving ? "Saving..." : undefined}</Spinner>
      </div>
    );
  } else if (loadFailed) {
    content = <p className={styles.errorMessage}>Failed to load the Care Connect configuration for this customer.</p>;
  } else {
    content = (
      <div className={styles.content}>
        <div className={styles.summary}>
          <span className={styles.summaryLabel}>Current edition</span>
          <span className={`${styles.badge} ${currentEdition === "lite" ? styles.badgeLite : styles.badgeFull}`}>
            {currentEdition === "lite" ? "Lite" : "Full"}
          </span>
        </div>
        {!hasConfiguration && (
          <p className={styles.note}>
            This customer has no Care Connect configuration record yet, so the default values are shown. Saving will
            create one.
          </p>
        )}
        <RadioButtonList
          name="careConnectEdition"
          accent={ProjectAccents.PeoplePlanner}
          value={selectedEdition}
          orientation="vertical"
          onChange={(e) => setSelectedEdition(e.value as CareConnectEdition)}
        >
          <RadioButton value="full" heading="Full">
            {EDITION_DESCRIPTIONS.full}
          </RadioButton>
          <RadioButton value="lite" heading="Lite">
            {EDITION_DESCRIPTIONS.lite}
          </RadioButton>
        </RadioButtonList>
        <div className={styles.field}>
          <Label>Daily email processing limit</Label>
          <Input
            style={{ width: "10em" }}
            type="number"
            min={1}
            value={limitInput}
            onChange={(e) => setLimitInput(e.target.value)}
          />
          {!limitIsValid && <p className={styles.errorMessage}>Enter a whole number greater than zero.</p>}
        </div>
      </div>
    );
  }

  return (
    <>
      <Modal
        visible={visible}
        accent={ProjectAccents.PeoplePlanner}
        heading="Configure Care Connect"
        onCancelButtonClick={() => handleClose()}
        onClose={() => handleClose()}
        primaryButtonProps={{
          text: "Save",
          onClick: () => setConfirmVisible(true),
          disabled: !canSave || loading || saving || loadFailed
        }}
      >
        {content}
      </Modal>
      {confirmVisible ? (
        <Confirm
          visible={confirmVisible}
          type="warning"
          accent={ProjectAccents.PeoplePlanner}
          heading="Are you sure?"
          onPrimaryButtonClick={handleSave}
          onCancelButtonClick={() => setConfirmVisible(false)}
          onClose={() => setConfirmVisible(false)}
        >
          <div className={styles.confirmContent}>
            <p>
              {hasConfiguration ? "The Care Connect configuration for " : "A Care Connect configuration record will be created for "}
              <strong>{selectedCustomer.name}</strong> ({selectedCustomer.clientId})
              {hasConfiguration ? " will be saved as:" : " with:"}
            </p>
            <ul>
              <li>
                Edition: <strong>{selectedEdition === "lite" ? "Lite" : "Full"}</strong>
              </li>
              <li>
                Daily email processing limit: <strong>{parsedLimit}</strong>
              </li>
            </ul>
            <p>{EDITION_DESCRIPTIONS[selectedEdition]}</p>
          </div>
        </Confirm>
      ) : undefined}
    </>
  );
};
